/**
 * +specs: bot kits for the Warrior specialisations (docs/design/classes/specs.md "Warrior", the
 * rotation sections). The APL presses evolved ids directly (the spec'd ClassDef binds them).
 * Auto, Group Finder strangers and the harness all play these.
 */
import { BLOODREAVER, BLOODREAVER_AURA, BASE_AURA, IRONCLAD, IRONCLAD_AURA, RAVAGER, RAVAGER_AURA } from '@keystone/content';
import type { ClassKit } from '../kits';

const I = IRONCLAD;
const IA = IRONCLAD_AURA;
const R = RAVAGER;
const RA = RAVAGER_AURA;
const BR = BLOODREAVER;
const BA = BLOODREAVER_AURA;
const FRESH = BASE_AURA.freshKill;

/** Ironclad: build to 5 plates, keep Shield Sweep rolling, cash plates in AoE / when low / before they fall off. */
export const ironcladKit: ClassKit = {
  classId: 'warrior',
  role: 'tank',
  ranged: false,
  interrupt: I.platedBash,
  majorDefensive: I.plateAegis,
  majorDefensiveAura: IA.plateAegisDr,
  grip: I.anchorChain,
  apl: [
    // Pull a runner or a caster to the shield.
    { ability: I.anchorChain, when: [{ type: 'targetDistAbove', yd: 8 }] },
    { ability: I.wreckage, when: [{ type: 'selfAura', auraId: FRESH }, { type: 'any', of: [{ type: 'selfHpBelow', pct: 0.85 }, { type: 'selfAuraRemainingBelow', auraId: FRESH, ms: 8000 }, { type: 'not', cond: { type: 'selfAura', auraId: IA.plating, minStacks: 4 } }] }] },
    // Iron Maiden in a real pull (3+) or a long single fight, then Shrapnel Burst inside it.
    { ability: I.ironMaiden, when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'enemiesNearSelf', atLeast: 3, radius: 8 }, { type: 'selfHpBelow', pct: 0.6 }, { type: 'targetHpAbove', pct: 0.6 }] }] },
    { ability: I.shrapnelBurst, when: [{ type: 'selfAura', auraId: IA.ironMaiden }, { type: 'selfAura', auraId: IA.plating, minStacks: 2 }] },
    // Plate Aegis at high plates when it hurts.
    { ability: I.plateAegis, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.5 }, { type: 'selfAura', auraId: IA.plating, minStacks: 3 }] },
    { ability: I.plateAegis, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.3 }] },
    // Cash plates: at 5 (Unstoppable glow), when low, or in a pack with 3+, or before they fall off.
    { ability: I.shrapnelBurst, when: [{ type: 'selfAura', auraId: IA.plating, minStacks: 5 }] },
    { ability: I.shrapnelBurst, when: [{ type: 'selfAura', auraId: IA.plating, minStacks: 3 }, { type: 'any', of: [{ type: 'selfHpBelow', pct: 0.55 }, { type: 'enemiesNearSelf', atLeast: 3, radius: 8 }] }] },
    { ability: I.shrapnelBurst, when: [{ type: 'selfAura', auraId: IA.plating }, { type: 'selfAuraRemainingBelow', auraId: IA.plating, ms: 3000 }] },
    { ability: I.shieldSweep, when: [{ type: 'inCombat' }] },
    { ability: I.rivetStrike },
  ],
};

/** Ravager: keep Overdrive up with Twin Reaping, Red Hunger on cooldown, Headsman when lit, Steel Cyclone fills. */
export const ravagerKit: ClassKit = {
  classId: 'warrior',
  role: 'dps',
  ranged: false,
  interrupt: R.hiltCrack,
  personalDefensive: R.wrathblood,
  apl: [
    { ability: R.breakneck, when: [{ type: 'targetDistAbove', yd: 8 }] },
    { ability: R.unchained, when: [{ type: 'inCombat' }, { type: 'any', of: [{ type: 'targetHpAbove', pct: 0.5 }, { type: 'enemiesNearSelf', atLeast: 3, radius: 8 }] }] },
    { ability: R.wrathblood, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.4 }] },
    // Overdrive about to drop (or not up): Twin Reaping first.
    { ability: R.twinReaping, when: [{ type: 'any', of: [{ type: 'selfLacksAura', auraId: RA.overdrive }, { type: 'selfAuraRemainingBelow', auraId: RA.overdrive, ms: 2500 }] }] },
    // AoE: spin first so the next Reapings cleave.
    { ability: R.steelCyclone, when: [{ type: 'enemiesNearSelf', atLeast: 3, radius: 8 }, { type: 'selfLacksAura', auraId: RA.cycloneEdge }, { type: 'selfLacksAura', auraId: RA.unchained }] },
    // Don't cap Bloodheat.
    { ability: R.twinReaping, when: [{ type: 'resource', resource: 'bloodheat', atLeast: 80 }] },
    { ability: R.redHunger },
    { ability: R.headsman, when: [{ type: 'selfAura', auraId: FRESH }, { type: 'selfHpBelow', pct: 0.9 }] },
    { ability: R.headsman, when: [{ type: 'targetHpBelow', pct: 0.35 }] },
    { ability: R.twinReaping },
    { ability: R.headsman, when: [{ type: 'selfAura', auraId: FRESH }] },
    { ability: R.steelCyclone, when: [{ type: 'inCombat' }] },
  ],
};

/**
 * Bloodreaver: dive with Bloodletting while healthy, stack 3 wounds, Bloodwrench at 40 Thirst (more
 * eagerly the lower you are), Sanguine Vigor / Crimson Trance as the safety net.
 */
export const bloodreaverKit: ClassKit = {
  classId: 'warrior',
  role: 'dps',
  ranged: false,
  interrupt: BR.crimsonKick,
  personalDefensive: BR.sanguineVigor,
  apl: [
    { ability: BR.gorecharge, when: [{ type: 'targetDistAbove', yd: 8 }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.5 } }] },
    { ability: BR.crimsonTrance, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.3 }] },
    { ability: BR.sanguineVigor, when: [{ type: 'inCombat' }, { type: 'selfHpBelow', pct: 0.3 }] },
    // Low: drink now.
    { ability: BR.bloodwrench, when: [{ type: 'selfHpBelow', pct: 0.4 }] },
    { ability: BR.finalDraught, when: [{ type: 'selfAura', auraId: FRESH }, { type: 'selfHpBelow', pct: 0.7 }] },
    // The dive: open a vein while healthy and in a fight that will last.
    { ability: BR.bloodletting, when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.6 } }, { type: 'targetHpAbove', pct: 0.25 }] },
    { ability: BR.rendingArc, when: [{ type: 'enemiesNearSelf', atLeast: 2, radius: 8 }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.35 } }] },
    // Full wounds (or Thirst about to cap): cash.
    { ability: BR.bloodwrench, when: [{ type: 'targetAura', auraId: BA.weepingWound, minStacks: 3 }] },
    { ability: BR.bloodwrench, when: [{ type: 'resource', resource: 'thirst', atLeast: 85 }] },
    { ability: BR.bloodwrench, when: [{ type: 'selfAura', auraId: BA.openVein }, { type: 'targetAura', auraId: BA.weepingWound, minStacks: 2 }] },
    { ability: BR.finalDraught, when: [{ type: 'targetHpBelow', pct: 0.35 }] },
    { ability: BR.finalDraught, when: [{ type: 'selfAura', auraId: FRESH }] },
    { ability: BR.rendingArc, when: [{ type: 'inCombat' }, { type: 'not', cond: { type: 'selfHpBelow', pct: 0.35 } }, { type: 'targetAura', auraId: BA.weepingWound, minStacks: 3 }] },
    { ability: BR.bleedingEdge },
  ],
};

/** Spec id → kit. */
export const WARRIOR_SPEC_KITS: Readonly<Record<string, ClassKit>> = { ironclad: ironcladKit, ravager: ravagerKit, bloodreaver: bloodreaverKit };
